import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  useToast,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Heading,
  Divider,
  IconButton
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import SalesAgreementsApi from '../../../../api/salesAgreements';
import GovernmentDataApi from '../../../../api/governmentData';

const AddSalesAgreementModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    planType: '',
    monthlyFee: '',
    pilotEnabled: false,
    pilotDays: '',
    setupFeeAmount: '',
    withdrawalFeeAmount: '9.5',
    withdrawalFeePaidBy: 'employee',
    withdrawalFeeFreeMonthly: '0',
    customConditions: [], // Array of { text: string }
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const toast = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Custom conditions handlers
  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      customConditions: [...prev.customConditions, { text: '' }]
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      customConditions: prev.customConditions.filter((_, i) => i !== index)
    }));
  };

  const handleConditionChange = (index, value) => {
    // Limit to 80 characters
    const limitedValue = value.substring(0, 80);
    setFormData(prev => ({
      ...prev,
      customConditions: prev.customConditions.map((condition, i) =>
        i === index ? { text: limitedValue } : condition
      )
    }));
  };

  const handleCompanyIdChange = async (companyId) => {
    handleInputChange('companyId', companyId);

    // Auto-fill company name if company ID is entered (9 digits)
    if (companyId.trim() && companyId.length === 9) {
      try {
        // Use government API to lookup company name by ID (same as ContactDetailsStep)
        const result = await GovernmentDataApi.getCompanyByGovId(companyId);
        if (result.success) {
          const company = result.company;
          const companyName = company['שם חברה'] || company.company_name || '';
          handleInputChange('companyName', companyName);

          toast({
            title: 'נמצאה חברה',
            description: `נמצאה חברה: ${companyName}`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'חברה לא נמצאה',
            description: result.errorMessage,
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Error looking up company:', error);
        toast({
          title: 'שגיאה בחיפוש',
          description: 'אירעה שגיאה בחיפוש החברה. אנא נסו שוב.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } else if (!companyId.trim()) {
      // Clear company name if company ID is cleared
      handleInputChange('companyName', '');
    }
  };

  const validateForm = () => {
    const requiredFields = ['companyId', 'companyName', 'contactName', 'contactPhone', 'monthlyFee'];

    // Check required fields
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        return { isValid: false, message: 'אנא מלא את כל השדות הנדרשים' };
      }
    }

    // Validate phone number format (exactly 10 digits)
    if (formData.contactPhone) {
      const phoneDigits = formData.contactPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        return { isValid: false, message: 'מספר הטלפון חייב להכיל בדיוק 10 ספרות' };
      }
    }

    // Validate email format if provided
    if (formData.contactEmail && formData.contactEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contactEmail)) {
        return { isValid: false, message: 'כתובת האימייל אינה תקינה' };
      }
    }

    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      toast({
        title: 'שגיאה',
        description: validation.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Prepare data for API - let backend handle defaults
      const apiData = {
        companyId: formData.companyId,
        companyName: formData.companyName,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail || null,
        monthlyFee: parseFloat(formData.monthlyFee),
        // Optional fields - backend will apply defaults if missing
        planType: formData.planType ? formData.planType.toLowerCase() : undefined, // Normalize to lowercase, let backend default to 'basic'
        // Pilot configuration
        pilotEnabled: formData.pilotEnabled,
        pilotDays: formData.pilotEnabled ? parseInt(formData.pilotDays) || 0 : undefined,
        // Setup fee configuration
        setupFeeAmount: formData.setupFeeAmount && parseFloat(formData.setupFeeAmount) > 0
          ? parseFloat(formData.setupFeeAmount)
          : undefined,
        // Withdrawal fee configuration - provide individual fields for backend merging
        withdrawalFeeJson: JSON.stringify({
          amount: formData.withdrawalFeeAmount ? parseFloat(formData.withdrawalFeeAmount) : 9.5,
          paidBy: formData.withdrawalFeePaidBy || 'employee',
          freeMonthlyCount: formData.withdrawalFeeFreeMonthly ? parseInt(formData.withdrawalFeeFreeMonthly) : 0
        }),
        // Custom conditions - filter out empty conditions
        customConditionsJson: formData.customConditions.filter(c => c.text.trim()).length > 0
          ? JSON.stringify(formData.customConditions.filter(c => c.text.trim()))
          : undefined
      };

      await SalesAgreementsApi.createSalesAgreement(apiData);

      toast({
        title: 'הצלחה',
        description: 'הסכם המכירות נוצר בהצלחה',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setFormData({
        companyId: '',
        companyName: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        planType: '',
        monthlyFee: '',
        pilotEnabled: false,
        pilotDays: '',
        setupFeeAmount: '',
        withdrawalFeeAmount: '9.5',
        withdrawalFeePaidBy: 'employee',
        withdrawalFeeFreeMonthly: '0',
        customConditions: [],
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating sales agreement:', error);
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה ביצירת הסכם המכירות',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      companyId: '',
      companyName: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      planType: '',
      monthlyFee: '',
      pilotEnabled: false,
      pilotDays: '',
      setupFeeAmount: '',
      withdrawalFeeAmount: '9.5',
      withdrawalFeePaidBy: 'employee',
      withdrawalFeeFreeMonthly: '0',
      customConditions: [],
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>
          <Heading size="lg" color={textColor}>
            הוספת הסכם מכירות חדש
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Company Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor}>
              פרטי החברה
            </Text>
            <Divider />
            
            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>ח.פ החברה</FormLabel>
                <Input
                  value={formData.companyId}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleCompanyIdChange(value);
                  }}
                  placeholder="123456789"
                  maxLength={9}
                  dir="ltr"
                  textAlign="left"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>שם החברה</FormLabel>
                <Input
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="הכנס שם החברה"
                />
              </FormControl>
            </HStack>

            {/* Contact Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              פרטי איש הקשר
            </Text>
            <Divider />
            
            <FormControl isRequired>
              <FormLabel>שם מלא של איש הקשר</FormLabel>
              <Input
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                placeholder="הכנס שם מלא"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl
                isRequired
                isInvalid={formData.contactPhone && formData.contactPhone.replace(/\D/g, '').length > 0 && formData.contactPhone.replace(/\D/g, '').length !== 10}
              >
                <FormLabel>טלפון איש הקשר</FormLabel>
                <Input
                  value={formData.contactPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      handleInputChange('contactPhone', value);
                    }
                  }}
                  placeholder="0501234567"
                  maxLength={10}
                  dir="ltr"
                  textAlign="left"
                />
              </FormControl>
              
              <FormControl
                isInvalid={formData.contactEmail && formData.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)}
              >
                <FormLabel>כתובת אימייל</FormLabel>
                <Input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  placeholder="הכנס כתובת אימייל"
                />
              </FormControl>
            </HStack>

            {/* Plan Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              פרטי המנוי
            </Text>
            <Divider />
            
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>מנוי</FormLabel>
                <Select
                  value={formData.planType}
                  onChange={(e) => handleInputChange('planType', e.target.value)}
                  placeholder="בחר סוג מנוי"
                >
                  <option value="Basic">Basic</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Premium">Premium</option>
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>ריטיינר (₪)</FormLabel>
                <NumberInput
                  value={formData.monthlyFee}
                  onChange={(value) => handleInputChange('monthlyFee', value)}
                  min={0}
                >
                  <NumberInputField placeholder="הכנס סכום" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            {/* Pilot Information */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              פיילוט
            </Text>
            <Divider />

            <Checkbox
              isChecked={formData.pilotEnabled}
              onChange={(e) => handleInputChange('pilotEnabled', e.target.checked)}
            >
              פיילוט
            </Checkbox>

            {formData.pilotEnabled && (
              <FormControl>
                <FormLabel>מספר ימי פיילוט</FormLabel>
                <NumberInput
                  value={formData.pilotDays}
                  onChange={(value) => handleInputChange('pilotDays', value)}
                  min={1}
                >
                  <NumberInputField placeholder="הכנס מספר ימים" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            )}

            {/* Setup Fee */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              דמי הקמה
            </Text>
            <Divider />

            <FormControl>
              <FormLabel>דמי הקמה (₪)</FormLabel>
              <NumberInput
                value={formData.setupFeeAmount}
                onChange={(value) => handleInputChange('setupFeeAmount', value)}
                min={0}
              >
                <NumberInputField placeholder="הכנס סכום (אופציונלי)" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Withdrawal Fee */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              עמלת משיכה
            </Text>
            <Divider />

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>סכום עמלה (₪)</FormLabel>
                <NumberInput
                  value={formData.withdrawalFeeAmount}
                  onChange={(value) => handleInputChange('withdrawalFeeAmount', value)}
                  min={0}
                  step={0.1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>משלם עמלת משיכה</FormLabel>
                <Select
                  value={formData.withdrawalFeePaidBy}
                  onChange={(e) => handleInputChange('withdrawalFeePaidBy', e.target.value)}
                >
                  <option value="employee">עובד</option>
                  <option value="employer">מעסיק</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>משיכות חודשיות בחינם</FormLabel>
              <NumberInput
                value={formData.withdrawalFeeFreeMonthly}
                onChange={(value) => handleInputChange('withdrawalFeeFreeMonthly', value)}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Custom Conditions */}
            <Text fontSize="md" fontWeight="bold" color={textColor} mt={4}>
              תנאים מותאמים אישית
            </Text>
            <Divider />

            <VStack spacing={2} align="stretch">
              {formData.customConditions.map((condition, index) => (
                <HStack key={index} spacing={2}>
                  <Input
                    value={condition.text}
                    onChange={(e) => handleConditionChange(index, e.target.value)}
                    placeholder="הכנס תנאי (עד 80 תווים)"
                    maxLength={80}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCondition(index)}
                    aria-label="הסר תנאי"
                  />
                </HStack>
              ))}
              <Button
                leftIcon={<AddIcon />}
                variant="outline"
                colorScheme="blue"
                size="sm"
                onClick={handleAddCondition}
                alignSelf="flex-start"
              >
                הוסף תנאי
              </Button>
              <Text fontSize="xs" color="gray.500">
                תנאים אלו יופיעו תחת "תנאי השירות" בעמוד התנאים המסחריים
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            ביטול
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="יוצר..."
          >
            צור הסכם
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddSalesAgreementModal;
